import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { useApp } from '../context/AppContext';
import { Badge } from '../components/Badge';
import { RECOMMENDED_PROFILES } from '../data/rbacUiMeta';
import { RBAC_ACTION_KEYS, RBAC_MODULE_IDS, moduleLabelKey, permissionLabelKey, roleDescKey, roleTitleKey } from '../utils/rbacPermissions';
import { getFlowIaStatus } from '../services/flowIaService';

import { MODULE_VERSION } from '../version';

const ROLE_IDS = ['rrhh_admin', 'rrhh_manager', 'rrhh_operator', 'employee_self_service', 'finance_reviewer', 'viewer'];

const INTEGRATION_ROADMAP = [
  { labelKey: 'settings.integFlowLabel', statusKey: 'settings.integStatusPending', descKey: 'settings.integFlowDesc' },
  { labelKey: 'settings.integFinanceLabel', statusKey: 'settings.integStatusPending', descKey: 'settings.integFinanceDesc' },
  { labelKey: 'settings.integStorageLabel', statusKey: 'settings.integStatusPending', descKey: 'settings.integStorageDesc' },
  { labelKey: 'settings.integClockLabel', statusKey: 'settings.integStatusPending', descKey: 'settings.integClockDesc' },
  { labelKey: 'settings.integSignLabel', statusKey: 'settings.integStatusPending', descKey: 'settings.integSignDesc' },
];

const TABS = ['general', 'modules', 'roles', 'comms', 'storage', 'integrations', 'security', 'release'];

const FLOW_STEPS = ['flowStepLogin', 'flowStepValidate', 'flowStepTenant', 'flowStepRole', 'flowStepPerms', 'flowStepGuards'];

const INTEGRATION_FLOW_STEPS = [
  'integrationFlowStep1',
  'integrationFlowStep2',
  'integrationFlowStep3',
  'integrationFlowStep4',
  'integrationFlowStep5',
  'integrationFlowStep6',
  'integrationFlowStep7',
];

const FLOW_RC_STATUS_KEYS = [
  'flowRcStatusConceptual',
  'flowRcStatusEndpoint',
  'flowRcStatusTenant',
  'flowRcStatusModuleRoles',
  'flowRcStatusGranular',
  'flowRcStatusPendingReal',
];

const CONNECTOR_DEFS = [
  { id: 'email', titleKey: 'settings.connectorEmailTitle', descKey: 'settings.connectorEmailDesc', usageKey: 'settings.connectorEmailUsage' },
  { id: 'storage', titleKey: 'settings.connectorStorageTitle', descKey: 'settings.connectorStorageDesc', usageKey: 'settings.connectorStorageUsage' },
  { id: 'flow', titleKey: 'settings.connectorFlowTitle', descKey: 'settings.connectorFlowDesc', usageKey: 'settings.connectorFlowUsage' },
  { id: 'finance', titleKey: 'settings.connectorFinanceTitle', descKey: 'settings.connectorFinanceDesc', usageKey: 'settings.connectorFinanceUsage' },
  { id: 'sign', titleKey: 'settings.connectorSignTitle', descKey: 'settings.connectorSignDesc', usageKey: 'settings.connectorSignUsage' },
  { id: 'clock', titleKey: 'settings.connectorClockTitle', descKey: 'settings.connectorClockDesc', usageKey: 'settings.connectorClockUsage' },
];

export function Settings() {
  const { t, mockRole, setMockRole, permSummary, isProductionUi, authMe } = useApp();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [tab, setTab] = useState(/** @type {typeof TABS[number]} */ ('general'));
  const [mailCfg, setMailCfg] = useState(null);
  const [storageCfg, setStorageCfg] = useState(null);
  const [rolesCatalog, setRolesCatalog] = useState(null);
  const [flowIaStatus, setFlowIaStatus] = useState(null);
  const [flowIaStatusErr, setFlowIaStatusErr] = useState(false);

  useEffect(() => {
    setFlowIaStatusErr(false);
    getFlowIaStatus()
      .then((r) => {
        setFlowIaStatus(r);
      })
      .catch(() => {
        setFlowIaStatus(null);
        setFlowIaStatusErr(true);
      });
  }, []);

  useEffect(() => {
    if (tab !== 'storage' && tab !== 'release') return;
    api
      .get('/api/storage/config')
      .then((r) => setStorageCfg(r.data))
      .catch(() => setStorageCfg(null));
  }, [tab]);

  useEffect(() => {
    if (tab !== 'comms' && tab !== 'release') return;
    api
      .get('/api/rrhh-emails/config')
      .then((r) => setMailCfg(r.data))
      .catch(() => setMailCfg(null));
  }, [tab]);

  useEffect(() => {
    if (tab !== 'roles') return;
    api
      .get('/api/roles/catalog')
      .then((r) => setRolesCatalog(r.data))
      .catch(() => setRolesCatalog(null));
  }, [tab]);

  useEffect(() => {
    const q = searchParams.get('tab');
    if (q && TABS.includes(q)) setTab(q);
  }, [searchParams]);

  useEffect(() => {
    api
      .get('/api/settings')
      .then((r) => {
        setData(r.data);
        setFailed(false);
      })
      .catch(() => {
        setData(null);
        setFailed(true);
      });
  }, []);

  if (failed) {
    return <div className="empty-state empty-state--panel">{t('common.error')}</div>;
  }

  if (!data) {
    return <div className="empty-state empty-state--panel">{t('common.loading')}</div>;
  }

  const versionLabel = MODULE_VERSION;

  const flowIaModeLabel = (m) => {
    if (m === 'active') return t('settings.flowIaModeActive');
    if (m === 'pending_key') return t('settings.flowIaModePendingKey');
    if (m === 'disabled') return t('settings.flowIaModeDisabled');
    return t('settings.flowIaModeFallback');
  };

  return (
    <div className="page-stack settings-page--v16 settings-page--rbac">
      <header className="settings-v16-hero">
        <div>
          <p className="settings-v16-hero__eyebrow">{t('settings.heroEyebrow')}</p>
          <h2 className="settings-v16-hero__title">{t('settings.heroTitle')}</h2>
          <p className="settings-v16-hero__lead">{t('settings.heroLead')}</p>
        </div>
        <span className="version-pill version-pill--hero" title={versionLabel}>
          {versionLabel}
        </span>
      </header>

      <div className="settings-v16-tabs settings-v16-tabs--wrap" role="tablist" aria-label={t('settings.tabsAria')}>
        {TABS.map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            className={`settings-v16-tab${tab === id ? ' is-active' : ''}`}
            onClick={() => setTab(id)}
          >
            {t(`settings.tab_${id}`)}
          </button>
        ))}
      </div>

      <div className="settings-v16-panels">
        {tab === 'general' && (
          <section className="settings-v16-section" aria-labelledby="settings-tab-general">
            <div className="settings-v16-grid-2">
              <div className="panel panel--elevated settings-v16-card">
                <h3 id="settings-tab-general" className="settings-v16-card__title">
                  {t('settings.sectionLanguage')}
                </h3>
                <p className="panel__muted">{t('settings.defaultLang')}</p>
                <p className="settings-v16-value">
                  <strong>{data.default_language}</strong>
                </p>
              </div>
              <div className="panel panel--elevated settings-v16-card">
                <h3 className="settings-v16-card__title">{t('settings.sectionCurrency')}</h3>
                <p className="panel__muted">{t('settings.defaultCurrency')}</p>
                <p className="settings-v16-value">
                  <strong>{data.default_currency}</strong>
                </p>
              </div>
            </div>
            <div className="panel panel--elevated settings-v16-card settings-v16-card--full">
              <h3 className="settings-v16-card__title">{t('settings.pfWellbeingTitle')}</h3>
              <p className="panel__muted text-sm">{t('settings.pfWellbeingStatus')}: <strong>{t('settings.pfWellbeingStatusActive')}</strong></p>
              <ul className="pi-card-list" style={{ marginTop: '0.75rem' }}>
                <li>{t('settings.pfWellbeingPrivacy1')}</li>
                <li>{t('settings.pfWellbeingPrivacy2')}</li>
                <li>{t('settings.pfWellbeingFuture1')}</li>
                <li>{t('settings.pfWellbeingFuture2')}</li>
              </ul>
            </div>
            <div className="panel panel--elevated settings-v16-card settings-v16-card--full">
              <h3 className="settings-v16-card__title">{t('settings.version')}</h3>
              <p className="mono panel__muted version-mono">{versionLabel}</p>
            </div>
          </section>
        )}

        {tab === 'modules' && (
          <section className="settings-v16-section">
            <div className="panel panel--elevated settings-v16-card settings-v16-card--full">
              <h3 className="settings-v16-card__title">{t('settings.sectionModules')}</h3>
              <p className="panel__muted">{t('settings.visibleModules')}</p>
              <ul className="chip-list chip-list--relaxed">
                {data.visible_modules?.map((m) => (
                  <li key={m} className="chip">
                    {m}
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {tab === 'roles' && (
          <section className="settings-v16-section settings-rbac-section">
            <div className="panel panel--elevated settings-v16-card settings-v16-card--full">
              <h3 className="settings-v16-card__title">{t('permissions.sectionTitle')}</h3>
              <p className="panel__muted">{t('settings.rolesIntro')}</p>
              <p className="panel__muted text-sm">{t('settings.rolesSyncNote')}</p>
              <div className="settings-rbac-mock-row">
                {!isProductionUi ? (
                <label className="field field--inline">
                  <span className="field__label">{t('permissions.mockRoleLabel')}</span>
                  <select className="input input--select" value={mockRole} onChange={(e) => setMockRole(e.target.value)}>
                    {ROLE_IDS.map((rid) => (
                      <option key={rid} value={rid}>
                        {t(roleTitleKey(rid))}
                      </option>
                    ))}
                  </select>
                </label>
                ) : (
                  <p className="panel__muted text-sm">
                    {authMe?.name} ({authMe?.role}) — {authMe?.email}
                  </p>
                )}
                {permSummary && (
                  <Badge variant={permSummary.source === 'local_demo' ? 'pending' : 'active'}>
                    {t(`permissions.source_${permSummary.source}`)}
                  </Badge>
                )}
              </div>
            </div>

            <div className="settings-v16-grid-2 settings-rbac-role-grid">
              {(rolesCatalog?.roles || ROLE_IDS.map((id) => ({ id, label: t(roleTitleKey(id)), description: t(roleDescKey(id)) }))).map(
                (role) => (
                  <div key={role.id} className="panel panel--elevated settings-rbac-role-card">
                    <h4 className="settings-rbac-role-card__title">{role.label || t(roleTitleKey(role.id))}</h4>
                    <p className="panel__muted text-sm">{role.description || t(roleDescKey(role.id))}</p>
                    <p className="settings-rbac-role-card__code mono text-sm">
                      {t('permissions.internalCode')}: {role.id}
                    </p>
                  </div>
                ),
              )}
            </div>

            <div className="panel panel--elevated settings-v16-card settings-v16-card--full">
              <h3 className="settings-v16-card__title">{t('permissions.matrixTitle')}</h3>
              <p className="panel__muted text-sm">{t('permissions.matrixLegend')}</p>
              <div className="settings-rbac-matrix-wrap">
                <table className="data-table settings-rbac-matrix">
                  <thead>
                    <tr>
                      <th className="settings-rbac-matrix__sticky">{t('permissions.matrixModuleCol')}</th>
                      {RBAC_ACTION_KEYS.map((a) => (
                        <th key={a} title={t(permissionLabelKey(a))}>
                          <span className="settings-rbac-matrix__action">{t(permissionLabelKey(a))}</span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {RBAC_MODULE_IDS.map((mid) => (
                      <tr key={mid}>
                        <td className="settings-rbac-matrix__sticky">
                          <strong>{t(moduleLabelKey(mid))}</strong>
                        </td>
                        {RBAC_ACTION_KEYS.map((a) => {
                          const ok = permSummary?.permissions?.[mid]?.[a];
                          return (
                            <td key={`${mid}-${a}`} className="settings-rbac-matrix__cell">
                              {ok ? <span className="settings-rbac-check">✓</span> : <span className="settings-rbac-dash">—</span>}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="panel__muted text-sm">{t('permissions.matrixReadOnly')}</p>
            </div>

            <h3 className="settings-v16-subsection-title">{t('permissions.profilesTitle')}</h3>
            <div className="settings-v16-grid-2 settings-profile-grid">
              {RECOMMENDED_PROFILES.map((p) => (
                <div key={p.id} className={`panel panel--elevated settings-profile-card settings-profile-card--${p.tone}`}>
                  <h4 className="settings-profile-card__title">{t(`permissions.profile_${p.id}_title`)}</h4>
                  <p className="panel__muted text-sm">
                    <strong>{t('permissions.profileFor')}</strong> {t(`permissions.profile_${p.id}_for`)}
                  </p>
                  <p className="panel__muted text-sm">
                    <strong>{t('permissions.profileModules')}</strong> {t(`permissions.profile_${p.id}_modules`)}
                  </p>
                  <p className="panel__muted text-sm">
                    <strong>{t('permissions.profileRestrictions')}</strong> {t(`permissions.profile_${p.id}_restrictions`)}
                  </p>
                  <p className="panel__muted text-sm">
                    <strong>{t('permissions.profileRisk')}</strong> {t(`permissions.profile_${p.id}_risk`)}
                  </p>
                </div>
              ))}
            </div>

            <div className="panel panel--elevated settings-v16-card settings-v16-card--full settings-flow-panel">
              <h3 className="settings-v16-card__title">{t('permissions.flowSectionTitle')}</h3>
              <p className="panel__muted">{t('permissions.flowIntro')}</p>
              <ul className="settings-flow-bullets">
                <li>{t('permissions.flowBullet1')}</li>
                <li>{t('permissions.flowBullet2')}</li>
                <li>{t('permissions.flowBullet3')}</li>
                <li>{t('permissions.flowBullet4')}</li>
                <li>{t('permissions.flowBullet5')}</li>
                <li>{t('permissions.flowBullet6')}</li>
                <li>{t('permissions.flowBullet7')}</li>
              </ul>
              <p className="panel__muted text-sm">{t('permissions.flowNoSso')}</p>
              <div className="settings-flow-chain" aria-label={t('permissions.flowChainAria')}>
                {FLOW_STEPS.map((sk, i) => (
                  <React.Fragment key={sk}>
                    {i > 0 && <span className="settings-flow-arrow" aria-hidden />}
                    <span className="settings-flow-step">{t(`permissions.${sk}`)}</span>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </section>
        )}

        {tab === 'integrations' && (
          <section className="settings-v16-section">
            <div className="panel panel--elevated settings-v16-card settings-v16-card--full settings-flow-auth-rc">
              <h3 className="settings-v16-card__title">{t('settings.integrationFlowCenterTitle')}</h3>
              <p className="panel__muted">{t('settings.integrationFlowCenterIntro')}</p>
              <ol className="settings-integration-flow-ol">
                {INTEGRATION_FLOW_STEPS.map((sk) => (
                  <li key={sk}>{t(`settings.${sk}`)}</li>
                ))}
              </ol>
              <h4 className="settings-v16-card__subtitle">{t('settings.integrationFlowStatusTitle')}</h4>
              <ul className="settings-flow-rc-pills" aria-label={t('settings.integrationFlowStatusTitle')}>
                {FLOW_RC_STATUS_KEYS.map((sk) => (
                  <li key={sk}>
                    <Badge variant="product_prepared">{t(`settings.${sk}`)}</Badge>
                  </li>
                ))}
              </ul>
              <p className="panel__muted text-sm">{t('settings.integrationFlowNoSso')}</p>
            </div>

            <div className="settings-v16-grid-2">
              <div className="panel panel--elevated settings-v16-card">
                <h3 className="settings-v16-card__title">{t('settings.sectionFlow')}</h3>
                <p className="panel__muted">
                  <Badge variant={data.flow_auth_enabled ? 'product_prepared' : 'inactive'}>
                    {data.flow_auth_enabled ? t('settings.integrationsFlagPrepared') : t('settings.integrationsFlagOff')}
                  </Badge>{' '}
                  {t('settings.flowPrepared')}
                </p>
              </div>
              <div className="panel panel--elevated settings-v16-card">
                <h3 className="settings-v16-card__title">{t('settings.sectionFinance')}</h3>
                <p className="panel__muted">
                  <Badge variant={data.finance_integration_enabled ? 'product_prepared' : 'inactive'}>
                    {data.finance_integration_enabled ? t('settings.integrationsFlagPrepared') : t('settings.integrationsFlagOff')}
                  </Badge>{' '}
                  {t('settings.financePrepared')}
                </p>
              </div>
            </div>

            <div className="panel panel--elevated settings-v16-card settings-v16-card--full">
              <h3 className="settings-v16-card__title">{t('settings.flowIaCardTitle')}</h3>
              <p className="panel__muted">{t('settings.flowIaCardIntro')}</p>
              {flowIaStatusErr ? (
                <p className="panel__muted text-sm">{t('settings.flowIaErr')}</p>
              ) : !flowIaStatus ? (
                <p className="panel__muted text-sm">{t('settings.flowIaLoading')}</p>
              ) : (
                <ul className="settings-v16-integ-list" style={{ listStyle: 'none', padding: 0, margin: '0.75rem 0 0' }}>
                  <li className="panel__muted">
                    <strong>{t('settings.flowIaStatusLabel')}:</strong> {flowIaModeLabel(flowIaStatus.mode)}
                  </li>
                  <li className="panel__muted">
                    <strong>{t('settings.flowIaProviderLabel')}:</strong> {flowIaStatus.provider}
                  </li>
                  <li className="panel__muted">
                    <strong>{t('settings.flowIaModelLabel')}:</strong> {flowIaStatus.model}
                  </li>
                  <li className="panel__muted text-sm">
                    <strong>{t('settings.flowIaModuleVersionLabel')}:</strong> {flowIaStatus.module_version}
                  </li>
                </ul>
              )}
            </div>

            <h3 className="settings-v16-subsection-title">{t('settings.sectionFutureIntegrations')}</h3>
            <ul className="settings-v16-integ-list">
              {INTEGRATION_ROADMAP.map((item) => (
                <li key={item.labelKey} className="panel panel--elevated settings-v16-integ-card">
                  <div className="settings-v16-integ-card__head">
                    <span className="settings-v16-integ-card__label">{t(item.labelKey)}</span>
                    <span className="settings-future-pill">{t(item.statusKey)}</span>
                  </div>
                  <p className="panel__muted settings-v16-integ-card__desc">{t(item.descKey)}</p>
                </li>
              ))}
            </ul>

            <h3 className="settings-v16-subsection-title">{t('settings.lifecycleFlowsTitle')}</h3>
            <p className="panel__muted settings-v16-lifecycle-intro">{t('settings.lifecycleFlowsIntro')}</p>
            <ul className="settings-lifecycle-stack">
              <li className="panel panel--elevated settings-v16-card">
                <h4 className="settings-v16-card__subtitle">{t('settings.lifecycleOnTitle')}</h4>
                <p className="panel__muted">{t('settings.lifecycleOnBody')}</p>
              </li>
              <li className="panel panel--elevated settings-v16-card">
                <h4 className="settings-v16-card__subtitle">{t('settings.lifecycleOffTitle')}</h4>
                <p className="panel__muted">{t('settings.lifecycleOffBody')}</p>
              </li>
              <li className="panel panel--elevated settings-v16-card">
                <h4 className="settings-v16-card__subtitle">{t('settings.lifecycleDocsTitle')}</h4>
                <p className="panel__muted">{t('settings.lifecycleDocsBody')}</p>
              </li>
              <li className="panel panel--elevated settings-v16-card settings-v16-callout settings-v16-callout--future">
                <h4 className="settings-v16-card__subtitle">{t('settings.lifecycleAutoTitle')}</h4>
                <p className="panel__muted">{t('settings.lifecycleAutoBody')}</p>
              </li>
              <li className="panel panel--elevated settings-v16-card">
                <h4 className="settings-v16-card__subtitle">{t('settings.lifecycleIntegTitle')}</h4>
                <p className="panel__muted">{t('settings.lifecycleIntegBody')}</p>
              </li>
            </ul>
          </section>
        )}

        {tab === 'comms' && (
          <section className="settings-v16-section settings-comms-email" aria-labelledby="settings-tab-comms">
            <div className="panel panel--elevated settings-v16-card settings-v16-card--full">
              <h3 id="settings-tab-comms" className="settings-v16-card__title">
                {t('settings.commsEmailTitle')}
              </h3>
              <p className="panel__muted">{t('settings.commsEmailLead')}</p>
            </div>
            <ul className="settings-comms-stack">
              <li className="panel panel--elevated settings-v16-card">
                <h4 className="settings-v16-card__subtitle">{t('settings.commsLiveTitle')}</h4>
                {mailCfg ? (
                  <>
                    <p className="panel__muted">
                      <strong>{t('settings.commsProviderLabel')}:</strong> Resend
                    </p>
                    <p className="panel__muted">
                      <strong>{t('settings.commsStatusRuntime')}:</strong>{' '}
                      {t(`communications.emailConfigStatus_${mailCfg.status}`)}
                    </p>
                    <p className="panel__muted">
                      <strong>{t('settings.commsModeRuntime')}:</strong>{' '}
                      {mailCfg.mode === 'real' ? t('communications.emailModeReal') : t('communications.emailModeSimulation')}
                    </p>
                    <p className="panel__muted">
                      <strong>{t('settings.commsFromRuntime')}:</strong> {mailCfg.from}
                    </p>
                    <p className="panel__muted">
                      <strong>{t('settings.commsReplyRuntime')}:</strong>{' '}
                      {mailCfg.reply_to_configured ? t('settings.commsReplyYes') : t('settings.commsReplyNo')}
                    </p>
                  </>
                ) : (
                  <p className="panel__muted">{t('common.loading')}</p>
                )}
              </li>
              <li className="panel panel--elevated settings-v16-card">
                <h4 className="settings-v16-card__subtitle">{t('settings.commsEngineLabel')}</h4>
                <p className="panel__muted">{t('settings.commsEngineValue')}</p>
              </li>
              <li className="panel panel--elevated settings-v16-card">
                <h4 className="settings-v16-card__subtitle">{t('settings.commsLogsLabel')}</h4>
                <p className="panel__muted">{t('settings.commsLogsValue')}</p>
              </li>
              <li className="panel panel--elevated settings-v16-card settings-v16-callout">
                <h4 className="settings-v16-card__subtitle">{t('settings.commsMarketingSepTitle')}</h4>
                <p className="panel__muted">{t('settings.commsMarketingSepBody')}</p>
              </li>
            </ul>
          </section>
        )}

        {tab === 'storage' && (
          <section className="settings-v16-section">
            <div className="panel panel--elevated settings-v16-card settings-v16-card--full">
              <h3 className="settings-v16-card__title">{t('settings.sectionStorageDocumental')}</h3>
              <p className="panel__muted">{t('settings.storageDocRule')}</p>
              <p className="panel__muted text-sm">{t('settings.storageHint')}</p>
            </div>
            <div className="settings-v16-grid-2">
              <div className="panel panel--elevated settings-v16-card settings-v16-callout">
                <h3 className="settings-v16-card__title">{t('settings.storageCardCurrentTitle')}</h3>
                <p className="panel__muted">{t('settings.storageDocState')}</p>
              </div>
              <div className="panel panel--elevated settings-v16-card settings-v16-callout settings-v16-callout--future">
                <h3 className="settings-v16-card__title">{t('settings.storageCardFutureTitle')}</h3>
                <p className="panel__muted">{t('settings.storageDocFuture')}</p>
              </div>
            </div>
            <div className="panel panel--elevated settings-v16-card settings-v16-card--full">
              <h3 className="settings-v16-card__title">{t('settings.storageApiTitle')}</h3>
              {!storageCfg ? (
                <p className="panel__muted">{t('settings.storageApiLoading')}</p>
              ) : (
                <ul className="settings-v16-integ-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  <li className="panel__muted">
                    <strong>{t('settings.storageApiMode')}:</strong> {storageCfg.mode}
                  </li>
                  <li className="panel__muted">
                    <strong>{t('settings.storageApiStatus')}:</strong> {storageCfg.status}
                  </li>
                  <li className="panel__muted">
                    <strong>{t('settings.storageApiEnabled')}:</strong> {storageCfg.enabled ? t('legajo.yes') : t('legajo.no')}
                  </li>
                  <li className="panel__muted">
                    <strong>{t('settings.storageApiBucket')}:</strong>{' '}
                    {storageCfg.bucket_configured ? t('legajo.yes') : t('legajo.no')}
                  </li>
                  <li className="panel__muted">
                    <strong>{t('settings.storageApiCreds')}:</strong>{' '}
                    {storageCfg.credentials_configured ? t('legajo.yes') : t('legajo.no')}
                  </li>
                  <li className="panel__muted">
                    <strong>{t('settings.storageApiMax')}:</strong> {storageCfg.max_upload_mb} MB
                  </li>
                  <li className="panel__muted">
                    <strong>{t('settings.storageApiMimes')}:</strong> {(storageCfg.allowed_mime_types || []).join(', ')}
                  </li>
                </ul>
              )}
              <p className="panel__muted text-sm" style={{ marginTop: '1rem' }}>
                {t('settings.storageApiVarsBody')}
              </p>
            </div>
          </section>
        )}

        {tab === 'security' && (
          <section className="settings-v16-section">
            <div className="panel panel--elevated settings-v16-card settings-v16-card--full">
              <h3 className="settings-v16-card__title">{t('settings.sectionSecurity')}</h3>
              <p className="panel__muted">{t('settings.architectureNote')}</p>
              <div className="settings-v16-spacer" />
              <h4 className="settings-v16-card__subtitle">{t('settings.tenantMode')}</h4>
              <p className="panel__muted">
                <span className="settings-status settings-status--ok">●</span> {t('settings.tenantPrepared')}
              </p>
              <p className="panel__muted text-sm">{t('settings.securityFlowHint')}</p>
            </div>
          </section>
        )}

        {tab === 'release' && (
          <section className="settings-v16-section settings-release-rc" aria-labelledby="settings-tab-release">
            <div className="panel panel--elevated settings-v16-card settings-v16-card--full">
              <h3 id="settings-tab-release" className="settings-v16-card__title">
                {t('settings.releaseModuleStateTitle')}
              </h3>
              <p className="panel__muted">{t('settings.releaseModuleStateLead')}</p>
              <dl className="settings-release-dl">
                <div>
                  <dt>{t('settings.releaseRowVersion')}</dt>
                  <dd>{versionLabel}</dd>
                </div>
                <div>
                  <dt>{t('settings.releaseRowState')}</dt>
                  <dd>{t('settings.releaseRowStateValue')}</dd>
                </div>
                <div>
                  <dt>{t('settings.releaseRowMode')}</dt>
                  <dd>{t('settings.releaseRowModeValue')}</dd>
                </div>
                <div>
                  <dt>{t('settings.releaseRowEmail')}</dt>
                  <dd>
                    {!mailCfg ? (
                      t('common.loading')
                    ) : mailCfg.mode === 'real' && mailCfg.status === 'ready' ? (
                      t('settings.releaseEmailReal')
                    ) : (
                      t('settings.releaseEmailDemo')
                    )}
                  </dd>
                </div>
                <div>
                  <dt>{t('settings.releaseRowStorage')}</dt>
                  <dd>
                    {!storageCfg ? (
                      t('common.loading')
                    ) : storageCfg.status === 'ready' ? (
                      t('settings.releaseStorageSpaces')
                    ) : storageCfg.status === 'metadata_only' ? (
                      t('settings.releaseStorageMeta')
                    ) : (
                      t('settings.releaseStoragePending')
                    )}
                  </dd>
                </div>
                <div>
                  <dt>{t('settings.releaseRowFlow')}</dt>
                  <dd>{t('settings.releaseFlowPrepared')}</dd>
                </div>
                <div>
                  <dt>{t('settings.releaseRowFinance')}</dt>
                  <dd>{t('settings.releaseFinancePrepared')}</dd>
                </div>
              </dl>
              <p className="panel__muted text-sm">{t('rc.demoEnvironmentNotice')}</p>
            </div>

            <div className="panel panel--elevated settings-v16-card settings-v16-card--full">
              <h3 className="settings-v16-card__title">{t('settings.releaseChecklistTitle')}</h3>
              <ul className="settings-release-checklist">
                <li>
                  <strong>{t('settings.releaseCheckEmailTitle')}</strong>
                  <span className="panel__muted">
                    {!mailCfg
                      ? t('common.loading')
                      : t(`communications.emailConfigStatus_${mailCfg.status}`) || mailCfg.status}
                  </span>
                  <span className="panel__muted text-sm">{t('settings.releaseCheckEmailPending')}</span>
                </li>
                <li>
                  <strong>{t('settings.releaseCheckStorageTitle')}</strong>
                  <span className="panel__muted">
                    {!storageCfg
                      ? t('common.loading')
                      : t(`dashboard.storageStatus_${storageCfg.status}`) || storageCfg.status}
                  </span>
                  <span className="panel__muted text-sm">{t('settings.releaseCheckStoragePending')}</span>
                </li>
                <li>
                  <strong>{t('settings.releaseCheckFlowTitle')}</strong>
                  <span className="panel__muted">{t('settings.releaseCheckFlowCurrent')}</span>
                  <span className="panel__muted text-sm">{t('settings.releaseCheckFlowPending')}</span>
                </li>
                <li>
                  <strong>{t('settings.releaseCheckFinanceTitle')}</strong>
                  <span className="panel__muted">{t('settings.releaseCheckFinanceCurrent')}</span>
                  <span className="panel__muted text-sm">{t('settings.releaseCheckFinancePending')}</span>
                </li>
                <li>
                  <strong>{t('settings.releaseCheckMobileTitle')}</strong>
                  <span className="panel__muted">{t('settings.releaseCheckMobileCurrent')}</span>
                  <span className="panel__muted text-sm">{t('settings.releaseCheckMobilePending')}</span>
                </li>
                <li>
                  <strong>{t('settings.releaseCheckPermTitle')}</strong>
                  <span className="panel__muted">{t('settings.releaseCheckPermCurrent')}</span>
                  <span className="panel__muted text-sm">{t('settings.releaseCheckPermPending')}</span>
                </li>
              </ul>
            </div>

            <h3 className="settings-v16-subsection-title">{t('settings.connectorsSectionTitle')}</h3>
            <div className="settings-connectors-grid">
              {CONNECTOR_DEFS.map((c) => {
                let statusLabel = t('settings.connectorStateFuture');
                let badgeVariant = /** @type {'product_prepared' | 'pending' | 'active'} */ ('pending');
                if (c.id === 'email') {
                  if (!mailCfg) statusLabel = t('common.loading');
                  else if (mailCfg.mode === 'real' && mailCfg.status === 'ready') {
                    statusLabel = t('settings.connectorStateEmailReal');
                    badgeVariant = 'active';
                  } else {
                    statusLabel = t('settings.connectorStateEmailDemo');
                    badgeVariant = 'product_prepared';
                  }
                }
                if (c.id === 'storage') {
                  if (!storageCfg) statusLabel = t('common.loading');
                  else if (storageCfg.status === 'ready') {
                    statusLabel = t('settings.connectorStateStorageReady');
                    badgeVariant = 'active';
                  } else if (storageCfg.status === 'metadata_only') {
                    statusLabel = t('settings.connectorStateStorageMeta');
                    badgeVariant = 'product_prepared';
                  } else {
                    statusLabel = t('settings.connectorStateStoragePending');
                    badgeVariant = 'pending';
                  }
                }
                if (c.id === 'flow' || c.id === 'finance') {
                  statusLabel = t('settings.connectorStatePrepared');
                  badgeVariant = 'product_prepared';
                }
                return (
                  <article key={c.id} className="panel panel--elevated settings-connector-card">
                    <div className="settings-connector-card__head">
                      <h4 className="settings-v16-card__subtitle">{t(c.titleKey)}</h4>
                      <Badge variant={badgeVariant}>{statusLabel}</Badge>
                    </div>
                    <p className="panel__muted text-sm">{t(c.descKey)}</p>
                    <p className="panel__muted text-sm settings-connector-card__usage">{t(c.usageKey)}</p>
                  </article>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
